import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Lightweight modal with backdrop. X (top right) closes via onClose.
 * Optional OK button via onConfirm.
 */
export const Modal = ({
  open,
  title,
  onClose,
  onConfirm,
  confirmLabel = "OK",
  testIdPrefix = "modal",
  children,
  footer,
  maxWidthClass = "max-w-2xl",
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-overlay`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        data-testid={`${testIdPrefix}-content`}
        className={`relative bg-white border border-[#e8e6df] rounded-sm shadow-2xl w-full ${maxWidthClass} max-h-[85vh] flex flex-col`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 md:px-8 py-5 border-b border-[#e8e6df] shrink-0">
          <h3
            data-testid={`${testIdPrefix}-title`}
            className="font-display text-[18px] md:text-[20px] font-bold tracking-tight text-[#0a1128] pr-6"
          >
            {title}
          </h3>
          <button
            type="button"
            data-testid={`${testIdPrefix}-close`}
            onClick={() => onClose?.()}
            aria-label="Schließen"
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full text-[#505a74] hover:bg-[#f4f3ed] hover:text-[#0a1128] transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div
          data-testid={`${testIdPrefix}-body`}
          className="px-6 md:px-8 py-6 overflow-y-auto text-[14px] leading-relaxed text-[#0a1128] flex-1"
        >
          {children}
        </div>

        {/* Footer (custom) */}
        {footer && (
          <div className="px-6 md:px-8 py-4 border-t border-[#e8e6df] shrink-0 bg-white">
            {footer}
          </div>
        )}

        {/* Footer (single-confirm-button convenience) */}
        {!footer && onConfirm && (
          <div className="px-6 md:px-8 py-4 border-t border-[#e8e6df] flex items-center justify-end gap-3 shrink-0 bg-white">
            <button
              type="button"
              data-testid={`${testIdPrefix}-confirm`}
              onClick={() => onConfirm?.()}
              className="btn-primary px-7 py-3 rounded-full text-[13px] font-semibold"
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
